// src/services/notificationService.js
// Live schema: user_id, hoa_id, type, title, body, is_read, read_at,
// triggered_by, action_url, related_entity_type, related_entity_id, created_at.

import { supabase } from '../lib/supabase';
import { withSupabaseQueryTimeout } from '../lib/supabaseTimeout';

const NOTIFICATION_TIMEOUT_MS = 30000;
const STAFF_ROLES = new Set(['admin', 'management', 'board']);

const normalizeRole = (role) => {
  if (role === 'homeowner') return 'member';
  return role || 'member';
};

const audienceForRole = (role) => {
  const normalized = normalizeRole(role);
  if (normalized === 'member') return 'homeowner';
  return normalized;
};

const unique = (values = []) => [...new Set(values.filter(Boolean))];

const loadActiveProfilesWithRoles = async (hoaId) => {
  if (!hoaId) return [];

  const { data: profiles, error: profilesError } = await withSupabaseQueryTimeout(
    supabase
      .from('user_profiles')
      .select('id, email, full_name, is_acc_member, is_active, hoa_id')
      .eq('hoa_id', hoaId)
      .eq('is_active', true),
    'Loading notification recipients',
    NOTIFICATION_TIMEOUT_MS
  );

  if (profilesError) throw profilesError;
  if (!profiles?.length) return [];

  const profileIds = profiles.map(profile => profile.id);
  const { data: roleRows, error: rolesError } = await withSupabaseQueryTimeout(
    supabase
      .from('user_roles')
      .select('user_id, roles(name)')
      .eq('hoa_id', hoaId)
      .eq('is_active', true)
      .in('user_id', profileIds),
    'Loading notification recipient roles',
    NOTIFICATION_TIMEOUT_MS
  );

  if (rolesError) throw rolesError;

  const rolesByUser = new Map();
  (roleRows || []).forEach(row => {
    if (!rolesByUser.has(row.user_id)) {
      rolesByUser.set(row.user_id, normalizeRole(row.roles?.name));
    }
  });

  return profiles.map(profile => ({
    ...profile,
    role: rolesByUser.get(profile.id) || 'member',
  }));
};

const profileCanSeeDocument = (profile, document) => {
  const role = normalizeRole(profile.role);
  const level = String(document?.confidentiality_level || document?.visibility || 'member').replace(/s$/, '');

  if (level === 'public' || document?.visible_to_homeowners === true) return true;
  if (level === 'admin') return role === 'admin';
  if (level === 'board') return STAFF_ROLES.has(role);
  return STAFF_ROLES.has(role);
};

const profileCanSeeEvent = (profile, event) => {
  const role = normalizeRole(profile.role);
  if (role === 'admin') return true;

  const visibleRoles = event?.visible_to_roles?.length ? event.visible_to_roles : ['board', 'management', 'homeowner'];
  return visibleRoles.includes(audienceForRole(role));
};

const insertNotifications = async ({
  hoaId,
  userIds,
  type,
  title,
  body,
  actionUrl,
  triggeredBy,
  relatedEntityType,
  relatedEntityId,
}) => {
  const recipientIds = unique(userIds);
  if (!hoaId || !recipientIds.length || !type || !title) return { data: [], error: null };

  const rows = recipientIds.map(userId => ({
    hoa_id: hoaId,
    user_id: userId,
    type,
    title,
    body: body || title,
    action_url: actionUrl || null,
    is_read: false,
    triggered_by: triggeredBy || null,
    related_entity_type: relatedEntityType || null,
    related_entity_id: relatedEntityId || null,
  }));

  const result = await withSupabaseQueryTimeout(
    supabase
      .from('notifications')
      .insert(rows)
      .select('id'),
    'Creating notifications',
    NOTIFICATION_TIMEOUT_MS
  );

  if (result.error) {
    console.warn('notificationService.insertNotifications error:', result.error.message);
  }

  return result;
};

export const notificationService = {
  create: insertNotifications,

  createForUsers: insertNotifications,

  getRecipientsForDocument: async ({ hoaId, document, excludeUserId }) => {
    const profiles = await loadActiveProfilesWithRoles(hoaId);
    return profiles
      .filter(profile => profile.id !== excludeUserId)
      .filter(profile => profileCanSeeDocument(profile, document))
      .map(profile => profile.id);
  },

  getRecipientsForEvent: async ({ hoaId, event, excludeUserId }) => {
    const profiles = await loadActiveProfilesWithRoles(hoaId);
    return profiles
      .filter(profile => profile.id !== excludeUserId)
      .filter(profile => profileCanSeeEvent(profile, event))
      .map(profile => profile.id);
  },

  getACCRecipientIds: async ({ hoaId, excludeUserId, includeUserId } = {}) => {
    // Members cannot read other profiles; the SECURITY DEFINER function returns
    // only the ids of active ACC members in the caller's own HOA.
    let accIds = [];
    if (hoaId) {
      try {
        const { data, error } = await supabase.rpc('get_acc_recipient_ids');
        if (error) {
          console.warn('get_acc_recipient_ids failed:', error.message);
        } else {
          accIds = Array.isArray(data) ? data : [];
        }
      } catch (e) {
        console.warn('get_acc_recipient_ids failed:', e?.message || e);
      }
    }

    const recipientIds = accIds.filter(id => id !== excludeUserId);
    if (includeUserId) recipientIds.push(includeUserId);
    return unique(recipientIds);
  },

  notifyDocumentUploaded: async ({ document, actorUserId }) => {
    const hoaId = document?.hoa_id;
    const documentId = document?.id;
    if (!hoaId || !documentId) return { data: [], error: null };

    const title = document.title || document.name || document.file_name || 'New document';
    const recipientIds = await notificationService.getRecipientsForDocument({
      hoaId,
      document,
      excludeUserId: actorUserId,
    });

    return insertNotifications({
      hoaId,
      userIds: recipientIds,
      type: 'document_upload',
      title: `New document: ${title}`,
      body: `${title} was added to the document library.`,
      actionUrl: `/portal?tab=documents&document=${encodeURIComponent(documentId)}`,
      triggeredBy: actorUserId,
      relatedEntityType: 'document',
      relatedEntityId: documentId,
    });
  },

  notifyEventCreated: async ({ event, actorUserId }) => {
    const hoaId = event?.hoa_id;
    const eventId = event?.id;
    if (!hoaId || !eventId) return { data: [], error: null };

    const title = event.title || 'New event';
    const recipientIds = await notificationService.getRecipientsForEvent({
      hoaId,
      event,
      excludeUserId: actorUserId,
    });

    return insertNotifications({
      hoaId,
      userIds: recipientIds,
      type: 'event_created',
      title: `New event: ${title}`,
      body: `${title} was added to the community calendar.`,
      actionUrl: `/portal?tab=events&event=${encodeURIComponent(eventId)}`,
      triggeredBy: actorUserId,
      relatedEntityType: 'board_meeting',
      relatedEntityId: eventId,
    });
  },

  /** Fetch all notifications for a user, newest first. */
  getForUser: async (userId) => {
    const { data, error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('notifications')
        .select('id, type, title, body, is_read, read_at, action_url, related_entity_type, related_entity_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3),
      'Loading notifications',
      NOTIFICATION_TIMEOUT_MS
    );

    if (error) {
      console.warn('notificationService.getForUser error:', error.message);
      return [];
    }

    return (data || []).map((n) => ({
      id: n.id,
      event_type: n.type,
      title: n.title || '',
      message: n.body || n.title || '',
      read: n.is_read ?? false,
      created_at: n.created_at,
      action_url: n.action_url,
      related_entity_type: n.related_entity_type,
      related_entity_id: n.related_entity_id,
    }));
  },

  /** Get unread count for a user. */
  getUnreadCount: async (userId) => {
    const { count, error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false),
      'Loading unread notification count',
      NOTIFICATION_TIMEOUT_MS
    );

    if (error) return 0;
    return count || 0;
  },

  /** Mark a single notification as read. */
  markRead: async (notificationId) => {
    const { error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId),
      'Marking notification read',
      NOTIFICATION_TIMEOUT_MS
    );

    if (error) console.warn('notificationService.markRead error:', error.message);
  },

  /** Mark all unread notifications for the clicked entity as read. */
  markEntityNotificationsRead: async ({ userId, entityType, entityId, notificationId }) => {
    if (!entityType || !entityId) {
      await notificationService.markRead(notificationId);
      return;
    }

    const { error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('related_entity_type', entityType)
        .eq('related_entity_id', entityId)
        .eq('is_read', false),
      'Marking related notifications read',
      NOTIFICATION_TIMEOUT_MS
    );

    if (error) console.warn('notificationService.markEntityNotificationsRead error:', error.message);
  },

  /** Mark all notifications for a user as read. */
  markAllRead: async (userId) => {
    const { error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false),
      'Marking all notifications read',
      NOTIFICATION_TIMEOUT_MS
    );

    if (error) console.warn('notificationService.markAllRead error:', error.message);
  },

  /** Subscribe to new notifications for a user via Supabase Realtime. */
  subscribeForUser: (userId, onNewNotification) => {
    return supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        onNewNotification
      )
      .subscribe();
  },

  unsubscribe: (channel) => {
    supabase.removeChannel(channel);
  },
};
